import React, { useState, useEffect, useRef } from "react";
import { FiFileText, FiCheck, FiDownload, FiLoader } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { saveContactDetails } from "../../actions/contact-details-actions";
import { getPendingAgreements, recordDocumentConsent, UnitDocument } from "../../actions/document-actions";
import "./ContactDetailsModal.scss";

interface ContactDetailsModalProps {
    isOpen: boolean;
    initialFirstName?: string | null;
    initialLastName?: string | null;
    initialEmail?: string | null;
    onSaved: (firstName: string, lastName: string, email: string) => void;
}

type ModalStep = 'loading' | 'agreement' | 'contact';

const ContactDetailsModal: React.FC<ContactDetailsModalProps> = ({
    isOpen,
    initialFirstName,
    initialLastName,
    initialEmail,
    onSaved,
}) => {
    const { t } = useTranslation();
    const [step, setStep] = useState<ModalStep>('loading');
    const [pendingAgreements, setPendingAgreements] = useState<UnitDocument[]>([]);
    const [currentAgreementIndex, setCurrentAgreementIndex] = useState(0);
    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
    const [isAgreeingToAgreement, setIsAgreeingToAgreement] = useState(false);
    const [isPdfLoading, setIsPdfLoading] = useState(true);
    const iframeContainerRef = useRef<HTMLDivElement>(null);

    const [firstName, setFirstName] = useState(initialFirstName || "");
    const [lastName, setLastName] = useState(initialLastName || "");
    const [email, setEmail] = useState(initialEmail || "");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Load pending agreements when modal opens
    useEffect(() => {
        if (isOpen) {
            loadPendingAgreements();
        }
    }, [isOpen]);

    // Update state if initial values change
    useEffect(() => {
        setFirstName(initialFirstName || "");
        setLastName(initialLastName || "");
        setEmail(initialEmail || "");
    }, [initialFirstName, initialLastName, initialEmail]);

    // Reset scroll state when agreement changes
    useEffect(() => {
        setHasScrolledToBottom(false);
        setIsPdfLoading(true);
    }, [currentAgreementIndex]);

    const loadPendingAgreements = async () => {
        setStep('loading');
        try {
            const response = await getPendingAgreements();
            const agreements = response.data;

            if (agreements.length > 0) {
                setPendingAgreements(agreements);
                setCurrentAgreementIndex(0);
                setStep('agreement');
            } else {
                // No agreements, go directly to contact details
                setStep('contact');
            }
        } catch {
            // If error loading agreements, proceed to contact details
            setStep('contact');
        }
    };

    const handleScroll = () => {
        if (iframeContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = iframeContainerRef.current;
            // Consider scrolled to bottom if within 50px
            if (scrollTop + clientHeight >= scrollHeight - 50) {
                setHasScrolledToBottom(true);
            }
        }
    };

    const handleIframeLoad = () => {
        setIsPdfLoading(false);
        // Auto-enable agree button after PDF loads (iframe scroll detection is not reliable)
        setHasScrolledToBottom(true);
    };

    const handleAgreeToAgreement = async () => {
        const currentAgreement = pendingAgreements[currentAgreementIndex];
        if (!currentAgreement) return;

        setIsAgreeingToAgreement(true);
        setError(null);

        try {
            await recordDocumentConsent(currentAgreement.id);

            if (currentAgreementIndex < pendingAgreements.length - 1) {
                // More agreements to show
                setCurrentAgreementIndex(prev => prev + 1);
            } else {
                // All agreements done, proceed to contact details
                setStep('contact');
            }
        } catch (err: any) {
            const message = err.response?.data || "Failed to record agreement. Please try again.";
            setError(message);
        } finally {
            setIsAgreeingToAgreement(false);
        }
    };

    const validateEmail = (email: string): boolean => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        // Validate fields
        const trimmedFirstName = firstName.trim();
        const trimmedLastName = lastName.trim();
        const trimmedEmail = email.trim();

        if (!trimmedFirstName || !trimmedLastName || !trimmedEmail) {
            setError(t("contactDetails.allFieldsRequired"));
            return;
        }

        if (!validateEmail(trimmedEmail)) {
            setError(t("contactDetails.validEmailRequired"));
            return;
        }

        setIsSubmitting(true);

        try {
            await saveContactDetails({
                firstName: trimmedFirstName,
                lastName: trimmedLastName,
                email: trimmedEmail,
            });

            onSaved(trimmedFirstName, trimmedLastName, trimmedEmail);
        } catch (err: any) {
            const message = err.response?.data || "Failed to save contact details. Please try again.";
            setError(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const downloadDocument = (doc: UnitDocument) => {
        window.open(doc.blobUrl, '_blank');
    };

    // Use Google Docs Viewer to display PDF inline (prevents auto-download)
    const getViewerUrl = (url: string): string => {
        const encodedUrl = encodeURIComponent(url);
        return `https://docs.google.com/gview?url=${encodedUrl}&embedded=true`;
    };

    if (!isOpen) return null;

    const currentAgreement = pendingAgreements[currentAgreementIndex];

    // Loading state
    if (step === 'loading') {
        return (
            <div className="contact-drawer">
                <div className="contact-drawer__backdrop" />
                <div className="contact-drawer__content contact-drawer__content--centered">
                    <FiLoader className="contact-drawer__loading-spinner" size={32} />
                    <p className="contact-drawer__loading-text">{t("common.loading")}</p>
                </div>
            </div>
        );
    }

    // Agreement step
    if (step === 'agreement' && currentAgreement) {
        return (
            <div className="contact-drawer">
                <div className="contact-drawer__backdrop" />
                <div className="contact-drawer__content contact-drawer__content--agreement">
                    <div className="contact-drawer__header">
                        <h3>Agreement Required</h3>
                        {pendingAgreements.length > 1 && (
                            <span className="contact-drawer__progress">
                                {currentAgreementIndex + 1} of {pendingAgreements.length}
                            </span>
                        )}
                    </div>

                    <p className="contact-drawer__subtitle">
                        Please review and agree to the following document to continue.
                    </p>

                    <div className="contact-drawer__agreement-info">
                        <div className="contact-drawer__agreement-icon">
                            <FiFileText size={24} />
                        </div>
                        <div className="contact-drawer__agreement-details">
                            <h4>{currentAgreement.name}</h4>
                            {currentAgreement.description && (
                                <p>{currentAgreement.description}</p>
                            )}
                        </div>
                        <button
                            className="contact-drawer__download-btn"
                            onClick={() => downloadDocument(currentAgreement)}
                            type="button"
                        >
                            <FiDownload size={18} />
                        </button>
                    </div>

                    {error && (
                        <div className="contact-drawer__error">
                            {error}
                        </div>
                    )}

                    <div
                        className="contact-drawer__pdf-container"
                        ref={iframeContainerRef}
                        onScroll={handleScroll}
                    >
                        {isPdfLoading && (
                            <div className="contact-drawer__pdf-loading">
                                <FiLoader className="contact-drawer__loading-spinner" size={24} />
                                <p>Loading document...</p>
                            </div>
                        )}
                        <iframe
                            src={getViewerUrl(currentAgreement.blobUrl)}
                            className="contact-drawer__pdf-iframe"
                            title={currentAgreement.name}
                            onLoad={handleIframeLoad}
                            style={{ opacity: isPdfLoading ? 0 : 1 }}
                        />
                    </div>

                    <button
                        onClick={handleAgreeToAgreement}
                        disabled={isAgreeingToAgreement || !hasScrolledToBottom}
                        className="contact-drawer__button"
                    >
                        {isAgreeingToAgreement ? (
                            <>
                                <span className="loading-spinner" />
                                Processing...
                            </>
                        ) : (
                            <>
                                <FiCheck size={18} />
                                I Agree
                            </>
                        )}
                    </button>
                </div>
            </div>
        );
    }

    // Contact details step
    return (
        <div className="contact-drawer">
            <div className="contact-drawer__backdrop" />
            <div className="contact-drawer__content">
                <div className="contact-drawer__header">
                    <h3>{t("contactDetails.title")}</h3>
                </div>

                <p className="contact-drawer__subtitle">
                    {t("contactDetails.subtitle")}
                </p>

                {error && (
                    <div className="contact-drawer__error">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="contact-drawer__form">
                    <div className="contact-drawer__field">
                        <label htmlFor="firstName">{t("contactDetails.firstName")} *</label>
                        <input
                            type="text"
                            id="firstName"
                            placeholder={t("contactDetails.firstNamePlaceholder")}
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            disabled={isSubmitting}
                            autoFocus
                        />
                    </div>

                    <div className="contact-drawer__field">
                        <label htmlFor="lastName">{t("contactDetails.lastName")} *</label>
                        <input
                            type="text"
                            id="lastName"
                            placeholder={t("contactDetails.lastNamePlaceholder")}
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            disabled={isSubmitting}
                        />
                    </div>

                    <div className="contact-drawer__field">
                        <label htmlFor="email">{t("contactDetails.email")} *</label>
                        <input
                            type="email"
                            id="email"
                            placeholder={t("contactDetails.emailPlaceholder")}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={isSubmitting}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="contact-drawer__button"
                    >
                        {isSubmitting ? (
                            <>
                                <span className="loading-spinner" />
                                {t("contactDetails.saving")}
                            </>
                        ) : (
                            t("contactDetails.continue")
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ContactDetailsModal;
